import React, { useState, useEffect, useCallback } from "react";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { useModal } from "../../components/ui/ModalProvider";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";
import { useRequestGuard } from "../../hooks/useRequestGuard";

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const ANON_KEY      = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── Small reusable components ───────────────────────────────────────────────

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      Not Connected
    </span>
  );
}

function Spinner({ size = 5 }) {
  return (
    <svg
      className={`animate-spin h-${size} w-${size}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EtsyAccounts() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startAccountsRequest = useRequestGuard();

  const [user, setUser]             = useState(null);
  const [accounts, setAccounts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [syncingId, setSyncingId]   = useState(null);   // accountId being synced
  const [syncResults, setSyncResults] = useState({});    // { [accountId]: { count, error } }

  // ── Read URL params (Etsy callback redirects back here) ────────────────────
  const params    = new URLSearchParams(window.location.search);
  const connected = params.get("connected");
  const error     = params.get("error");

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data?.user ?? null));
  }, []);

  // ── Load accounts ─────────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    const isCurrent = startAccountsRequest();
    if (!user || !activeProfile?.id) {
      setAccounts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) { setLoading(false); return; }

      const res = await fetch(`${FUNCTIONS_URL}/etsy-accounts?profileId=${encodeURIComponent(activeProfile.id)}`, {
        headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Unable to load Etsy accounts");
      if (isCurrent()) setAccounts(json.accounts ?? []);
    } catch (e) {
      if (isCurrent()) {
        console.error("Failed to load Etsy accounts:", e);
        setAccounts([]);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [user, activeProfile?.id, startAccountsRequest]);

  useEffect(() => {
    startAccountsRequest();
    setAccounts([]);
    setSyncResults({});
  }, [activeProfile?.id, startAccountsRequest]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Clean up URL params after load
  useEffect(() => {
    if (connected || error) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [connected, error]);

  // Show result toasts after redirect
  useEffect(() => {
    if (connected === "true") alert("✅ Etsy shop connected successfully!");
    if (error)               alert(`❌ Connection failed: ${friendlyError(error)}`);
  }, []);                                                 // eslint-disable-line

  // ── Connect / Reconnect ───────────────────────────────────────────────────
  const handleConnect = async () => {
    if (!user) { alert("Please sign in first."); return; }
    if (!activeProfile?.id) { alert("Please select a Gymplix profile first."); return; }
    try {
      const token = await getAccessToken();
      const res = await fetch(`${FUNCTIONS_URL}/etsy-oauth-initiate?profileId=${encodeURIComponent(activeProfile.id)}`, {
        headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY },
      });
      const json = await res.json();
      if (!res.ok || !json.url) throw new Error(json.error ?? "Failed to start OAuth");
      window.location.href = json.url;
    } catch (e) {
      alert(`❌ Could not connect: ${e.message}`);
    }
  };

  // ── Disconnect ────────────────────────────────────────────────────────────
  const handleDisconnect = async (account) => {
    if (!(await confirm(`Disconnect "${account.shop_name}" from Gymplix?`))) return;
    try {
      const token = await getAccessToken();
      await fetch(`${FUNCTIONS_URL}/etsy-accounts`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${token}`,
          apikey:         ANON_KEY,
        },
        body: JSON.stringify({ action: "disconnect", accountId: account.id, profileId: activeProfile.id }),
      });
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
    } catch (e) {
      alert("❌ Failed to disconnect. Please try again.");
    }
  };

  // ── Sync Listings (test connection) ──────────────────────────────────────
  const handleSyncListings = async (account) => {
    setSyncingId(account.id);
    setSyncResults((prev) => ({ ...prev, [account.id]: null }));
    try {
      const token = await getAccessToken();
      const res = await fetch(
        `${FUNCTIONS_URL}/etsy-listings?accountId=${encodeURIComponent(account.id)}&profileId=${encodeURIComponent(activeProfile.id)}`,
        { headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Sync failed");
      setSyncResults((prev) => ({
        ...prev,
        [account.id]: { count: json.listings?.length ?? json.total ?? 0 },
      }));
    } catch (e) {
      setSyncResults((prev) => ({
        ...prev,
        [account.id]: { error: e.message },
      }));
    } finally {
      setSyncingId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />

        <main className="main-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                     style={{ backgroundColor: "#FFF3E0" }}>
                  <EtsyLogo />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Etsy Account</h1>
                  <p className="text-xs text-gray-500">
                    Connect your Etsy shop to use listings as Pinterest pin content.
                  </p>
                </div>
              </div>
              <StatusBadge connected={accounts.length > 0} />
            </div>

            {/* Body */}
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <Spinner size={8} />
              </div>
            ) : accounts.length === 0 ? (
              <EmptyState onConnect={handleConnect} />
            ) : (
              <div className="space-y-4">
                {accounts.map((account) => (
                  <ShopCard
                    key={account.id}
                    account={account}
                    syncResult={syncResults[account.id]}
                    isSyncing={syncingId === account.id}
                    onReconnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onSync={handleSyncListings}
                  />
                ))}

                {/* Add another shop */}
                <button
                  type="button"
                  onClick={handleConnect}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center
                             hover:border-orange-400 hover:bg-orange-50 transition-colors group"
                >
                  <span className="text-2xl group-hover:scale-110 inline-block transition-transform">+</span>
                  <p className="text-sm text-gray-500 mt-1 group-hover:text-orange-600">
                    Connect another Etsy shop
                  </p>
                </button>
              </div>
            )}

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyState({ onConnect }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
      <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
           style={{ backgroundColor: "#FFF3E0" }}>
        <EtsyLogo size={32} />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">No Etsy Shop Connected</h2>
      <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
        Connect your Etsy shop to pull active listings directly into your Pinterest pin workflow.
      </p>
      <button
        type="button"
        onClick={onConnect}
        className="mt-6 px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors"
        style={{ backgroundColor: "#E55300" }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#c94900")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#E55300")}
      >
        Connect Etsy Account
      </button>
    </div>
  );
}

function ShopCard({ account, syncResult, isSyncing, onReconnect, onDisconnect, onSync }) {
  const connectedDate = account.connected_at
    ? new Date(account.connected_at).toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      })
    : "—";

  const tokenExpired = account.token_expires_at
    ? new Date(account.token_expires_at) < new Date()
    : false;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      {/* Top row */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center"
             style={{ backgroundColor: "#FFF3E0" }}>
          <EtsyLogo size={24} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 text-base">{account.shop_name}</h3>
            <StatusBadge connected={!tokenExpired} />
            {tokenExpired && (
              <span className="text-xs text-amber-600 font-medium">Token expired — please reconnect</span>
            )}
          </div>

          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
            <div className="flex gap-1.5">
              <dt className="font-medium text-gray-600">Shop ID:</dt>
              <dd>{account.shop_id}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="font-medium text-gray-600">Connected:</dt>
              <dd>{connectedDate}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Sync result banner */}
      {syncResult && (
        <div className={`mt-3 rounded-lg px-4 py-2 text-sm ${
          syncResult.error
            ? "bg-red-50 text-red-700 border border-red-200"
            : "bg-green-50 text-green-700 border border-green-200"
        }`}>
          {syncResult.error
            ? `Sync failed: ${syncResult.error}`
            : `✅ ${syncResult.count} active listing${syncResult.count !== 1 ? "s" : ""} found`}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={onReconnect}
          className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700
                     hover:bg-gray-50 transition-colors font-medium"
        >
          Reconnect
        </button>

        <button
          type="button"
          onClick={() => onSync(account)}
          disabled={isSyncing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ borderColor: "#E55300", color: "#E55300" }}
        >
          {isSyncing ? <><Spinner size={4} /> Syncing...</> : "Sync Listings"}
        </button>

        <button
          type="button"
          onClick={() => onDisconnect(account)}
          className="ml-auto px-3 py-1.5 rounded-md text-sm text-red-600 hover:bg-red-50
                     transition-colors font-medium border border-red-200"
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

function EtsyLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#E55300" aria-hidden="true">
      <rect x="6.5" y="3.5" width="11" height="2.5" rx="0.4" />
      <rect x="6.5" y="3.5" width="2.5" height="17" rx="0.4" />
      <rect x="6.5" y="10.5" width="7.5" height="2.5" rx="0.4" />
      <rect x="6.5" y="18" width="11" height="2.5" rx="0.4" />
    </svg>
  );
}

function friendlyError(code) {
  const map = {
    cancelled:      "You cancelled the connection.",
    invalid_state:  "Session mismatch. Please try again.",
    token_exchange: "Token exchange with Etsy failed.",
    user_fetch:     "Could not retrieve your Etsy profile.",
    shop_fetch:     "Could not retrieve your Etsy shop.",
    no_shop:        "No Etsy shop found on this account.",
    db_save:        "Failed to save account. Please try again.",
  };
  return map[code] ?? code;
}
